import { Link } from 'react-router-dom';
import { Archive, Copy, Eye, MoreHorizontal } from 'lucide-react';
import type { ActivitySubjectCard } from '@shared/types/activity-feed';
import { buildSubjectThreadPath } from '@/lib/thread-routes';
import {
  deriveActivityDisplayEntity,
  deriveActivityNextActionLabel,
  formatActivityAge,
} from './activity-ui';

interface ActivitySubjectTableRowProps {
  subject: ActivitySubjectCard;
  isMenuOpen: boolean;
  isArchiving: boolean;
  onOpen: (subject: ActivitySubjectCard) => void;
  onArchive: (subject: ActivitySubjectCard) => void;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

export const getActivitySubjectId = (subject: ActivitySubjectCard): string =>
  subject.subjectId ??
  subject.subjectKey ??
  `${subject.subjectKind}:${subject.subjectLabel}`;

const copySubjectLabel = async (subject: ActivitySubjectCard) => {
  const entity = deriveActivityDisplayEntity(subject);
  const text = entity.context ? `${entity.label} · ${entity.context}` : entity.label;

  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
};

export const ActivitySubjectTableRow = ({
  subject,
  isMenuOpen,
  isArchiving,
  onOpen,
  onArchive,
  onToggleMenu,
  onCloseMenu,
}: ActivitySubjectTableRowProps) => {
  const entity = deriveActivityDisplayEntity(subject);
  const threadPath = buildSubjectThreadPath(subject);
  const nextActionLabel =
    subject.recommendedActions?.[0]?.label ?? deriveActivityNextActionLabel(subject);

  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={() => onOpen(subject)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(subject);
        }
      }}
      className="group cursor-pointer transition-colors hover:bg-domino-muted/70 focus:outline-none focus-visible:bg-domino-muted"
    >
      <td className="px-3 py-4 align-middle sm:px-4">
        <div className="min-w-0">
          <Link
            to={threadPath}
            onClick={(event) => event.stopPropagation()}
            className="block truncate text-sm font-semibold tracking-tight text-domino-ink transition hover:text-domino-aubergine focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
          >
            {entity.label}
          </Link>
          {entity.context && (
            <p className="mt-1 truncate text-xs text-domino-inkSoft">{entity.context}</p>
          )}
          <p className="mt-1 text-xs text-domino-inkSoft">
            {formatActivityAge(subject.latestEventAt)}
          </p>
        </div>
      </td>
      <td className="px-3 py-4 align-middle sm:px-4">
        <p className="line-clamp-2 text-sm leading-5 text-domino-ink">
          {nextActionLabel}
        </p>
      </td>
      <td className="relative px-2 py-4 align-middle text-right sm:px-3">
        <button
          type="button"
          aria-label={`Actions pour ${entity.label}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleMenu();
          }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-aubergine focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>

        {isMenuOpen && (
          <div
            className="absolute right-4 top-14 z-20 w-52 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white py-1 text-left shadow-[var(--domino-shadow-elevated)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                onCloseMenu();
                onOpen(subject);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-domino-ink transition hover:bg-domino-muted"
            >
              <Eye className="h-4 w-4 text-domino-inkSoft" />
              Voir le résumé
            </button>
            <button
              type="button"
              onClick={() => {
                onCloseMenu();
                void copySubjectLabel(subject);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-domino-ink transition hover:bg-domino-muted"
            >
              <Copy className="h-4 w-4 text-domino-inkSoft" />
              Copier le nom
            </button>
            <button
              type="button"
              disabled={isArchiving}
              onClick={() => {
                onCloseMenu();
                onArchive(subject);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Archive className="h-4 w-4" />
              {isArchiving ? 'Archivage...' : 'Archiver'}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};
